import React, { useState, useEffect } from "react";
import {
  Button,
  FormControl,
  FormGroup,
  FormLabel,
  Modal,
  Pagination,
} from "react-bootstrap";
import EditIcon from "../../../images/edit-icon.svg";
import DeleteIcon from "../../../images/delete-icon.svg";
import PlusIcon from "../../../images/plus-icon.png";
import FileIcon from "../../../images/delete-file.svg";
import FileUploader from "../FileUploader/FileUpload.tsx";
import { Link } from "react-router-dom";
import { giftItemApi } from "./Api.tsx";
import { toast } from "react-toastify";

interface MonetaryItem {
  _id?: string;
  name: string;
  img: string;
}

interface MonetaryProps {
  onEdit: (item: MonetaryItem) => void;
  onDelete: (name: string) => void;
}

const Monetary: React.FC<MonetaryProps> = ({ onEdit, onDelete }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [selectedItem, setSelectedItem] = useState<MonetaryItem | null>(null);
  const [itemToDelete, setItemToDelete] = useState<MonetaryItem | null>(null);
  const [items, setItems] = useState<MonetaryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch monetary items on component mount
  useEffect(() => {
    fetchMonetaryItems(1, itemsPerPage);
  }, []);

  const fetchMonetaryItems = async (page: number = currentPage, limit: number = itemsPerPage) => {
    try {
      setLoading(true);
      const response = await giftItemApi.getAllGiftItems({ type: 'Monetary', page, limit });
      if (response.status === 'Success') {
        // Convert API data to match component interface
        const apiItems = response.data?.map((item: any) => ({
          _id: item._id,
          name: item.title,
          img: item.imageUrl || PlusIcon // Fallback to plus icon if no image
        })) || [];

        setItems(apiItems);

        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalCount(response.pagination.totalCount);
        }
      } else {
        toast.error(response.message || 'Failed to fetch monetary items');
      }
    } catch (error) {
      console.error('Error fetching monetary items:', error);
      toast.error('Failed to fetch monetary items');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchMonetaryItems(page, itemsPerPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchMonetaryItems(1, newItemsPerPage);
  };

  const handleAddClose = () => {
    setShowAddModal(false);
    setNewTitle("");
    setUploadedImageUrl("");
    setSelectedImageFile(null);
  };

  const handleAddShow = () => setShowAddModal(true);

  const handleAddSubmit = async () => {
    if (!newTitle.trim()) {
      toast.error('Title is required');
      return;
    }

    // Check if image is provided
    if (!selectedImageFile && !uploadedImageUrl) {
      toast.error('Image is required');
      return;
    }

    try {
      setLoading(true);
      
      const formData = new FormData();
      formData.append('type', 'Monetary');
      formData.append('title', newTitle.trim());
      
      // Add image file if selected
      if (selectedImageFile) {
        formData.append('image', selectedImageFile);
      } else if (uploadedImageUrl) {
        formData.append('imageUrl', uploadedImageUrl);
      }

      const response = await giftItemApi.createGiftItem(formData);
      
      if (response.status === 'Success') {
        toast.success(response.message || 'Monetary item created successfully');
        await fetchMonetaryItems(); // Refresh the list
        handleAddClose();
      } else {
        toast.error(response.message || 'Failed to create monetary item');
      }
    } catch (error: any) {
      console.error('Error creating monetary item:', error);
      toast.error(error.response?.data?.message || 'Failed to create monetary item');
    } finally {
      setLoading(false);
    }
  };

  const handleEditShow = (item: MonetaryItem) => {
    setSelectedItem(item);
    setEditTitle(item.name);
    setUploadedImageUrl(item.img);
    setSelectedImageFile(null);
    setShowEditModal(true);
  };

  const handleEditClose = () => {
    setShowEditModal(false);
    setSelectedItem(null);
    setEditTitle("");
    setUploadedImageUrl("");
    setSelectedImageFile(null);
  };

  const handleEditSubmit = async () => {
    if (!selectedItem || !editTitle.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!selectedItem._id) {
      toast.error('Cannot update item without ID');
      return;
    }

    try {
      setLoading(true);
      
      const formData = new FormData();
      formData.append('title', editTitle.trim());
      
      // Add image if a new file is selected or URL is different
      if (selectedImageFile) {
        formData.append('image', selectedImageFile);
      } else if (uploadedImageUrl && uploadedImageUrl !== selectedItem.img) {
        formData.append('imageUrl', uploadedImageUrl);
      }

      const response = await giftItemApi.updateGiftItem(selectedItem._id, formData);
      
      if (response.status === 'Success') {
        toast.success(response.message || 'Monetary item updated successfully');
        await fetchMonetaryItems(); // Refresh the list
        handleEditClose();
      } else {
        toast.error(response.message || 'Failed to update monetary item');
      }
    } catch (error: any) {
      console.error('Error updating monetary item:', error);
      toast.error(error.response?.data?.message || 'Failed to update monetary item');
    } finally {
      setLoading(false);
    }
  };

  // Delete Modal Handlers
  const handleDeleteShow = (item: MonetaryItem) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    if (!itemToDelete._id) {
      toast.error('Cannot delete item without ID');
      handleDeleteClose();
      return;
    }

    try {
      setLoading(true);
      
      const response = await giftItemApi.softDeleteGiftItem(itemToDelete._id);
      
      if (response.status === 'Success') {
        toast.success(response.message || 'Monetary item deleted successfully');
        await fetchMonetaryItems(); // Refresh the list
        handleDeleteClose();
      } else {
        toast.error(response.message || 'Failed to delete monetary item');
      }
    } catch (error: any) {
      console.error('Error deleting monetary item:', error);
      toast.error(error.response?.data?.message || 'Failed to delete monetary item');
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection from FileUploader component
  const handleFileSelect = (file: File | null) => {
    setSelectedImageFile(file);
    if (file) {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setUploadedImageUrl(previewUrl);
    }
  };

  // Handle file upload callback from FileUploader component (for backward compatibility)
  const handleFileUpload = (imageUrl: string) => {
    setUploadedImageUrl(imageUrl);
  };

  return (
    <>
      <div className="monetary-sec row">
        {loading && items.length === 0 ? (
          // Show only loader when initially loading
          <div className="col-12">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span className="ms-2">Loading monetary items...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Render existing items */}
            {items.map((item, idx) => (
              <div className="default-card col-xl-4 col-lg-6" key={item._id || idx}>
                <div className="default-card-in">
                  <figure>
                    <img 
                      src={item.img} 
                      alt={item.name}
                      onError={(e) => {
                        // Fallback to plus icon if image fails to load
                        e.currentTarget.src = PlusIcon;
                      }}
                    />
                  </figure>
                  <div className="cont">
                    <aside className="name" style={{
                      marginBottom: '8px',
                      textTransform: 'capitalize'
                    }} title={item.name}>
                      <span>{item.name.length > 25 ? item.name.slice(0, 25) + '...' : item.name}</span>
                    </aside>
                    <aside className="options d-flex justify-content-center gap-2">
                      <Button
                        variant="link"
                        className="p-0"
                        onClick={() => handleEditShow(item)}
                        disabled={loading}
                        title="Edit"
                      >
                        <img src={EditIcon} alt="Edit" />
                      </Button>
                      <Button
                        variant="link"
                        className="p-0"
                        onClick={() => handleDeleteShow(item)}
                        disabled={loading}
                        title="Delete"
                      >
                        <img src={DeleteIcon} alt="Delete" />
                      </Button>
                    </aside>
                  </div>
                </div>
              </div>
            ))}

            {/* Add New Block - Always show when not initially loading */}
            <div className="add-block col-xl-4 col-lg-6">
              <div className="add-block-in">
                <div onClick={handleAddShow} className="text-decoration-none" style={{ cursor: 'pointer' }}>
                  <div className="add-block-cont text-center">
                    <div className="table-wrapper">
                      <aside>
                        <figure>
                          <img src={PlusIcon} alt="Add New" />
                        </figure>
                        <h2>Add New</h2>
                      </aside>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {!loading && items.length > 0 && (
        <div className="physical-pagination-wrapper">
          <div className="pagination-info-section">
            <div className="pagination-showing">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries
            </div>
          </div>

          <div className="pagination-controls-section">
            <div className="pagination-per-page">
              <span className="pagination-label">Show</span>
              <select
                className="form-select form-select-sm pagination-select"
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>

            {totalPages > 1 && (
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                />
                {Array.from({ length: Math.min(totalPages, 4) }, (_, index) => {
                  let pageNumber;
                  if (totalPages <= 4) {
                    pageNumber = index + 1;
                  } else if (currentPage <= 2) {
                    pageNumber = index < 2 ? index + 1 : totalPages - (3 - index);
                  } else if (currentPage >= totalPages - 1) {
                    pageNumber = index < 2 ? index + 1 : totalPages - (3 - index);
                  } else {
                    pageNumber = currentPage - 1 + index;
                  }
                  const showEllipsis = totalPages > 4 &&
                    ((index === 1 && currentPage > 3) ||
                      (index === 2 && currentPage < totalPages - 2));
                  return (
                    <React.Fragment key={index}>
                      {showEllipsis && <span className="px-2">...</span>}
                      <Pagination.Item
                        key={pageNumber}
                        active={pageNumber === currentPage}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    </React.Fragment>
                  );
                })}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
              </Pagination>
            )}
          </div>
        </div>
      )}

      {/* Add Modal */}
      <Modal
        className="main-modal"
        show={showAddModal}
        onHide={handleAddClose}
        centered
      >
        <Modal.Header closeButton className="px-0">
          <Modal.Title>Add New Monetary</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-0 mt-2">
          <FormGroup className="form-group">
            <FormLabel>Title *</FormLabel>
            <FormControl
              type="text"
              placeholder="Enter title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              disabled={loading}
              maxLength={100}
            />
          </FormGroup>
          <FileUploader 
            onFileSelect={handleFileSelect}
            onFileUpload={handleFileUpload}
            showPreview={true}
          />
        </Modal.Body>
        <Modal.Footer className="px-0 border-0">
          <Button
            className="main-bttn"
            variant="primary"
            onClick={handleAddSubmit}
            disabled={!newTitle.trim() || (!selectedImageFile && !uploadedImageUrl) || loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Adding...
              </>
            ) : (
              'Add'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal
        className="main-modal"
        show={showEditModal}
        onHide={handleEditClose}
        centered
      >
        <Modal.Header closeButton className="px-0">
          <Modal.Title>Edit Monetary</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-0 mt-2">
          <FormGroup className="form-group">
            <FormLabel>Title *</FormLabel>
            <FormControl
              type="text"
              placeholder="Enter title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              disabled={loading}
              maxLength={100}
            />
          </FormGroup>
          <FileUploader 
            onFileSelect={handleFileSelect}
            onFileUpload={handleFileUpload}
            initialImageUrl={selectedItem?.img}
            showPreview={true}
          />
        </Modal.Body>
        <Modal.Footer className="px-0 border-0">
          <Button
            className="main-bttn"
            variant="primary"
            onClick={handleEditSubmit}
            disabled={!editTitle.trim() || loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={handleDeleteClose}
        centered
        className="delete-modal"
      >
        <Modal.Header closeButton style={{ border: 'none' }}>
          <Modal.Title style={{ textTransform: 'capitalize' }}>DELETE CONFIRMATION</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <img src={FileIcon} alt="" />
            </div>
            <div>
              <p className='mw-100' style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                Are you sure you want to delete "{itemToDelete?.name}"?
              </p>
              <p className='mw-100'>This action cannot be undone.</p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 text-left gap-3">
          <Button
            className="border-bttn"
            variant="secondary"
            onClick={handleDeleteClose}
            disabled={loading}
            style={{ width: "30%", margin: "0" }}
          >
            Cancel
          </Button>
          <Button
            className="main-bttn"
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={loading}
            style={{ width: "30%", margin: "0" }}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .delete-modal .btn-close {
          filter: brightness(0) invert(1);
        }
      `}</style>
    </>
  );
};

export default Monetary;